/**
 * Module-level singleton for cold-start notification deep links.
 *
 * On a cold launch the navigation tree (especially lazy-loaded inner navigators)
 * is not ready when getLastNotificationResponseAsync() resolves.  Rather than
 * retrying a 3-level nested navigate() call and losing the race, we store the
 * parsed intent here and let ProjectsScreen consume it once it mounts — at that
 * point the PlansStack navigator is fully initialized.
 */

export interface PendingNotificationNav {
  projectId: number;
  screen?:
    | 'ProjectSurveys'
    | 'ProjectChat'
    | 'ProjectMedications'
    | 'ProjectSmartReminders'
    | 'ProjectHealthAlerts'
    | 'ProjectFiles'
    | 'ProjectNotes'
    /** proactive_gap pushes about a finding, connector_review pushes and
     *  /project/:id/suggestions links: the care notes screen. */
    | 'AgentSuggestions'
    | 'ProjectWeeklyBriefing'
    | 'ProjectCommunicate'
    | 'ProjectSnapshots'
    | 'ProjectVault'
    | 'ProjectHome'
    | 'ProjectContacts'
    /** health_records_reconnect pushes: the Health Data screen. */
    | 'ProjectFasten'
    /** inbound_email pushes: the plan inbox. */
    | 'ProjectInbox'
    /** chat_import pushes: the finished import's review (ChatImportScreen). */
    | 'ChatImport';
  /** For inbound_email pushes answering a chat: the Ellie conversation to open (ProjectChat). */
  conversationId?: string;
  /** Source call to review; loading it still requires the current plan membership. */
  organizationCallId?: string;
  /** For chat_message pushes: room to pre-select inside ProjectCommunicate. */
  chatRoomId?: number;
  /** For weekly_briefing pushes: the briefing row to pre-select in the picker. */
  briefingId?: number;
  openPageName?: string;
  openSurveyName?: string;
  openQuestionName?: string;
  initialPrompt?: string;
  /** For proactive_gap push responses: the original question shown as read-only context
   *  so the user types their answer rather than re-submitting the question itself. */
  proactiveGapContext?: string;
  /** For reminder notification taps: the smart_key of the triggered reminder. */
  detailSmartKey?: string;
  /** For reminder notification taps: recurrence_rule.page_id from payload. */
  detailPageId?: string;
  /** For reminder/health alert detail sheet rendering. */
  detailKind?: 'smart_reminder' | 'health_alert';
  detailTitle?: string;
  detailBody?: string;
  detailOccurredAt?: string;
  detailAlertType?: string;
  detailContext?: Record<string, unknown>;
  /** For ProjectContacts: which ContactsScreen tab to open. */
  tab?: 'members' | 'contacts' | 'chat' | 'map';
  /** For member-request pushes: the member row to scroll to and open. */
  openMemberCognitoId?: string;
  /** For ProjectFiles: 'documents'. For ProjectCommunicate: 'chat' | 'inbox'.
   *  Notes is its own screen (ProjectNotes), not a Files tab. */
  initialTab?: 'documents' | 'inbox' | 'chat';
  /** Identifies a new notification tap even when its destination is unchanged. */
  notificationRequestId?: number;
  /** For ProjectNotes: saved note to open after loading the authenticated plan's notes. */
  openNoteId?: number;
  /** For AgentSuggestions: the care-agent finding a nudge was about, opened on arrival. */
  careNoteId?: number;
  /** For ChatImport: the import whose review to open. */
  importId?: number;
  /** For document_request pushes: auto-open the upload sheet on the Documents tab. */
  openUpload?: boolean;
  /** For document_request pushes: the slug to persist as document_type on upload. */
  prefillDocumentType?: string;
  /** For document_request pushes: the human-readable label shown in the upload sheet. */
  prefillDocumentLabel?: string;
  /** For calendar_followup pushes (ProjectNotes): auto-open TemplateFillSheet with this template. */
  openTemplateId?: number;
}

let _pending: PendingNotificationNav | null = null;

export function peekPendingNotificationNav(): PendingNotificationNav | null {
  return _pending;
}

type Listener = () => void;
const _listeners = new Set<Listener>();

export function setPendingNotificationNav(data: PendingNotificationNav): void {
  _pending = data;
  // Snapshot listeners — handlers may call consume + unsubscribe synchronously.
  Array.from(_listeners).forEach((l) => {
    try { l(); } catch { /* listener errors must not block other consumers */ }
  });
}

/** Reads and clears the pending intent — call once from ProjectsScreen on mount. */
export function consumePendingNotificationNav(): PendingNotificationNav | null {
  const data = _pending;
  _pending = null;
  return data;
}

/**
 * Subscribe to pending-nav writes. Fires AFTER setPendingNotificationNav has
 * stored the value, so the listener can call consumePendingNotificationNav()
 * to read+clear it.
 *
 * Needed because the cold-launch init effect in RootNavigator can store the
 * intent AFTER ProjectsScreen has already mounted and run its mount-only
 * consume effect — without this signal the intent would sit stale.
 */
export function subscribePendingNotificationNav(listener: Listener): () => void {
  _listeners.add(listener);
  return () => { _listeners.delete(listener); };
}
